import { ServiceInvocationError, ApplicationError, DurationExeededError, ValidationProblemDetailsError } from '@n2-common/services/errors';
import { getI18n } from 'react-i18next';
import { logErrorToServerside } from '@n2-common/services/serverSideLogging';
import { ActionOptions } from '@n2-common/ui/Container';
import { TFunction } from 'i18next';
import { FetchError } from '@n2-common/lib/fetchProxy';

export function errorToAction(
    error: unknown,
    formNameOrSubmitHandler: string | (() => void),
    applicationId: string | null | undefined,
    applicantId: string | null | undefined
): ActionOptions | undefined;

export function errorToAction(
    error: unknown,
    formNameOrSubmitHandler: string | (() => void),
    applicationId: string | null | undefined,
    applicantId: string | null | undefined,
    backend: Backend
): ActionOptions | undefined;

/**
 * Function to be used in in catch block, to parse catched error, and return a possible ActionOptions object,
 * to be used as argument for t.ex. DismissableErrorMessage, to show errormessage, and/or possible user-action.
 * @note This method is logging error serverside, if error has a solution (action).
 * @param error
 * @param formNameOrSubmitHandler Formname or submit-handler, to be used on resubmit types.
 * @param applicationId
 * @param applicantId
 * @returns
 */
export function errorToAction(
    error: unknown,
    formNameOrSubmitHandler: string | (() => void),
    applicationId: string | null | undefined,
    applicantId: string | null | undefined,
    backend: Backend = 'O1'
): ActionOptions | undefined {
    if (error instanceof DOMException && error.name == 'AbortError') {
        return { action: 'ignore', message: 'We ignore abort from abortsignal' };
    }

    const { t } = getI18n();
    let data: unknown = undefined;
    const [action, message] = (function parseError(): Readonly<[ActionOptions, string] | [undefined, undefined]> {
        if (error instanceof DurationExeededError) {
            // Only thing we can do, is to reload with applicationId, and see if state has changed.
            return [
                {
                    message: t<string>(`common:PollingError.Message`),
                    button: t<string>(`common:PollingError.ButtonText`),
                    action: 'replace',
                    url: getLoadApplicationUrl(location, error.applicationId, error.applicantId),
                },
                `${error.message}`,
            ] as const;
        }
        if (error instanceof ServiceInvocationError) {
            data = error.response;
            switch (error.method) {
                // Submit failed - ask for retry
                case 'O2Service.UploadDocuments':
                case 'O2Service.UserTaskComplete':
                case 'O2Service.DeleteDocument':
                case 'ApplicationApiService.PostLoanApplication':
                case 'ApplicationApiService.SetPpi':
                case 'CddService.PostToKompisApi':
                case 'ApplicationApiService.StartCreditCardSigning':
                case 'ApplicationApiService.SetAccountNumber': {
                    return [createRetryAction(t, formNameOrSubmitHandler), `Error calling ${error.method}: ${error.message}`] as const;
                }

                // Chained servicecall failed - Ask to load application, to se current state of application.
                case 'ApplicationApiService.GetApplicatId':
                    return [
                        {
                            message: t<string>(`common:ServiceInvocationError.StatusRequest.Message`),
                            button: t<string>(`common:ServiceInvocationError.StatusRequest.ButtonText`),
                            action: 'replace',
                            url: getLoadApplicationUrl(location, error.applicationId, error.applicantId),
                        },
                        `Error calling ${error.method}: ${error.message}`,
                    ] as const;
            }
        }
        if (error instanceof ApplicationError && error.applicationId) {
            return [
                {
                    message: t<string>(`common:GeneralApplicationError.Load.Message`),
                    button: t<string>(`common:GeneralApplicationError.Load.ButtonText`),
                    action: 'replace',
                    url: getLoadApplicationUrl(location, error.applicationId, error.applicantId),
                },
                error.message,
            ] as const;
        }
        if (error instanceof ValidationProblemDetailsError) {
            // This error should never be possible to experience on client - cs- and ss-validation should be in sync.
            return [
                {
                    message: `${error.message}<br />${Object.entries(error.errors)
                        .map(([k, v]) => `${k}: ${v.join(', ')}`)
                        .join('<br />')}`,
                    action: 'dismissable',
                },
                error.message,
            ] as const;
        }

        if (error instanceof FetchError) {
            return [
                {
                    message: t<string>('common:ServiceInvocationError.FetchError.Message'),
                    action: 'dismissable',
                },
                error.message,
            ] as const;
        }

        // This is typically network errors, endpoint problems - we just show an alert, and ask user to try submitting again.
        if (error instanceof TypeError) {
            return [
                {
                    message: t<string>('common:ServiceInvocationError.FetchError.Message'),
                    action: 'dismissable',
                },
                error.message,
            ] as const;
        }
        return [undefined, undefined] as const;
    })();

    if (action) {
        logErrorToServerside(`Action found for error: ${message}.`, applicationId, applicantId, data || error, backend);
    } else {
        logErrorToServerside(`Unhandled error`, applicationId, applicantId, data || error, backend);
    }
    return action;
}

export function createRetryAction(t: TFunction, formNameOrSubmitHandler: string | (() => void)) {
    const options = {
        message: t<string>(`common:ServiceInvocationError.PrimarySubmit.Message`),
        button: t<string>(`common:ServiceInvocationError.PrimarySubmit.ButtonText`),
    };
    return getResubmitAction(formNameOrSubmitHandler, options);
}

function getResubmitAction(formNameOrSubmitHandler: string | (() => void), options: { button: string; message: string }): ActionOptions {
    const isForm = typeof formNameOrSubmitHandler == 'string';
    return isForm
        ? { ...options, action: 'submit', formName: formNameOrSubmitHandler }
        : { ...options, action: 'click', onClick: formNameOrSubmitHandler };
}

function getLoadApplicationUrl(location: Location, applicationId: string | undefined, applicantId: string | undefined) {
    return applicationId
        ? `${location.pathname}?applicationId=${applicationId}${applicantId ? `&applicantId=${applicantId}` : ''}`
        : location.pathname;
}
